<!--
  @component
  Generates an SVG Beeswarm chart using a [d3-force simulation](https://github.com/d3/d3-force).
 -->
<script>
  import { getContext, untrack } from "svelte";
  import { forceSimulation, forceX, forceY, forceCollide } from "d3-force";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";

  const { data, xGet, width, height, zGet, z, custom } = getContext("LayerCake");

  /**
   * @typedef {Object} Props
   * @property {number} [r=4] - The circle radius size in pixels.
   * @property {number} [strokeWidth=1] - The circle's stroke width in pixels.
   * @property {string} [stroke='#fff'] - The circle's stroke color.
   * @property {number} [xStrength=0.95] - The value passed into the `.strength` method on `forceX`. See [the documentation](https://github.com/d3/d3-force#x_strength).
   * @property {number} [yStrength=0.075] - The value passed into the `.strength` method on `forceY`. See [the documentation](https://github.com/d3/d3-force#y_strength).
   * @property {Function} [getTitle] - An accessor function to get the field on the data element to display as a hover label using a `<title>` tag.
   * @property {any} [tooltip] - A reactive variable to bind the tooltip state.
   */

  /** @type {Props} */
  let {
    r = 4,
    strokeWidth = 1,
    stroke = "#fff",
    xStrength = 0.95,
    yStrength = 0.075,
    tooltip = $bindable()
  } = $props();

  /** @type {any[]} */
  let nodes = $state([]);

  // Matches the app's existing 760px breakpoint (see the @media rule below).
  let isMobile = $state(false);

  $effect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 760px)");
    isMobile = mql.matches;
    /** @param {MediaQueryListEvent} e */
    const handleChange = (e) => (isMobile = e.matches);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  });

  let simulation = $derived.by(() => {
    if (!$width || !$height || !$data.length) return null;

    const sim = forceSimulation($data.map((/** @type {any} */ d) => ({ ...d })))
      .force(
        "x",
        forceX()
          .x((d) => $xGet(d))
          .strength(xStrength)
      )
      .force(
        "y",
        forceY()
          .y($height / 2)
          .strength(yStrength)
      )
      .force("collide", forceCollide(r))
      .stop();

    return sim;
  });

  $effect(() => {
    if (!simulation) {
      nodes = [];
      return;
    }

    untrack(() => {
      // Run the simulation for a fixed number of iterations
      const maxIterations = Math.ceil(
        Math.log(simulation.alphaMin()) / Math.log(1 - simulation.alphaDecay())
      );

      for (let i = 0; i < maxIterations; ++i) {
        simulation.tick();
      }

      // Update nodes state to trigger reactivity
      nodes = [...simulation.nodes()];
    });
  });
</script>

<!-- <pattern id="diagonalHatch" patternUnits="userSpaceOnUse" width="4" height="4">
  <path
    d="M-1,1 l2,-2
           M0,4 l4,-4
           M3,5 l2,-2"
    style="stroke:#ECECEC; stroke-width:1"
  />
</pattern>
<g class="annotation box">
  <rect
    x={$xGet({ year_declared: 2022.75 })}
    y="0"
    width={$xGet({ year_declared: 2015.5 })}
    height={$height}
    fill="url(#diagonalHatch)"
  ></rect>
  <text
    x={isMobile
      ? $xGet({ year_declared: 2022.75 }) + $xGet({ year_declared: 2015.5 }) / 2
      : $xGet({ year_declared: 2022.48 })}
    y="-46"
    text-anchor={isMobile ? "middle" : "start"}>Eligibility window</text
  >
  <text
    x={isMobile
      ? $xGet({ year_declared: 2022.75 }) + $xGet({ year_declared: 2015.5 }) / 2
      : $xGet({ year_declared: 2022.53 })}
    y="-28"
    text-anchor={isMobile ? "middle" : "start"}>for EDA Disaster</text
  >
  <text
    x={isMobile
      ? $xGet({ year_declared: 2022.75 }) + $xGet({ year_declared: 2015.5 }) / 2
      : $xGet({ year_declared: 2022.7 })}
    y="-10"
    text-anchor={isMobile ? "middle" : "start"}>Supplemental</text
  >
</g> -->

<g class="bee-group">
  {#each nodes as node (node.id)}
    <circle
      fill={$custom.getColor($z(node))}
      {stroke}
      stroke-width={strokeWidth}
      cx={node.x}
      cy={node.y}
      {r}
      onmousemove={(e) =>
        (tooltip = {
          x: e.pageX,
          y: e.pageY,
          name: node.community_name,
          type: node.type,
          title: node.title,
          color: $custom.getColor($z(node)),
          year: node.year_declared,
          area: node.designated_area,
          applicant: node.lead_applicant,
          applicantIsStateOrTerritory: /^State of |^District of Columbia$|^American Samoa$|^Guam$|^Puerto Rico$|^U\.S\. Virgin Islands$|^Northern Mariana Islands$/.test(node.lead_applicant),
          applicantTakesArticle: !/^Guam$|^American Samoa$/.test(node.lead_applicant)
        })}
      onmouseleave={() => (tooltip = null)}
    >
    </circle>
  {/each}
</g>

<style>
  circle {
    cursor: pointer;
  }
  text {
    font-size: 14px;
    @media screen and (max-width: 760px) {
      font-size: 14px;
    }
  }
</style>
